export interface OffTrackBudgetConfig {
  /** Total seconds the racer may spend off the racing surface before the
   *  current lap is invalidated. Tuned for ~90s laps with ~1-2s of total
   *  excursions allowed (a wide kerb hop, a moment off in a slide). */
  budgetSeconds: number;
  /** Below this speed (m/s) off-track time doesn't count — a stopped or
   *  crawling car shouldn't burn budget while reorienting. */
  minSpeed: number;
}

const DEFAULTS: OffTrackBudgetConfig = {
  budgetSeconds: 1.5,
  minSpeed: 4,
};

/**
 * Accumulates off-track time per lap. When the budget is exceeded the lap
 * is marked invalid and stays invalid until `notifyLapBoundary` is called
 * (typically when the lap closes).
 *
 * Mirrors the small self-contained shape of `WrongWayDetector` so it can be
 * tested and reasoned about in isolation.
 */
export class OffTrackBudget {
  private accumulated = 0;
  private invalidated = false;
  private readonly config: OffTrackBudgetConfig;

  constructor(config: Partial<OffTrackBudgetConfig> = {}) {
    this.config = { ...DEFAULTS, ...config };
  }

  get isLapInvalidated(): boolean {
    return this.invalidated;
  }

  /** Seconds of off-track time accumulated on the current lap. */
  get usedSeconds(): number {
    return this.accumulated;
  }

  get budgetSeconds(): number {
    return this.config.budgetSeconds;
  }

  reset(): void {
    this.accumulated = 0;
    this.invalidated = false;
  }

  /** Mark the current lap invalid immediately (e.g. wrong-way detected). */
  invalidate(): void {
    this.invalidated = true;
  }

  notifyLapBoundary(): void {
    this.accumulated = 0;
    this.invalidated = false;
  }

  update(onRacingSurface: boolean, speed: number, dt: number): void {
    if (onRacingSurface) return;
    if (speed < this.config.minSpeed) return;
    this.accumulated += dt;
    if (this.accumulated >= this.config.budgetSeconds) {
      this.invalidated = true;
    }
  }
}
